import React, { useState, useRef, useEffect } from "react";
import { Sliders, Settings, X, Plus, Minus, ChevronDown } from "lucide-react";

export interface DifficultyDistribution {
  easy: number;
  medium: number;
  hard: number;
  [key: string]: number;
}

export interface ScoringScheme {
  right: number;
  wrong: number;
  [key: string]: number;
}

interface DifficultyPopoverProps {
  distribution: DifficultyDistribution;
  onChange: (dist: DifficultyDistribution) => void;
  maxQuestions: number;
  disabled?: boolean;
  maxAvailable?: DifficultyDistribution;
}

export const DifficultyPopover: React.FC<DifficultyPopoverProps> = ({ distribution, onChange, maxQuestions, disabled = false, maxAvailable }) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const totalAssigned = (distribution.easy || 0) + (distribution.medium || 0) + (distribution.hard || 0);
  const remaining = maxQuestions - totalAssigned;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateLevel = (level: keyof DifficultyDistribution, delta: number) => {
    const currentVal = distribution[level] || 0;
    const newVal = currentVal + delta;
    
    if (newVal < 0) return;
    if (delta > 0 && remaining <= 0) return; // Cannot exceed maxQuestions
    if (delta > 0 && maxAvailable && newVal > (maxAvailable[level] || 0)) return; // Cannot exceed maxAvailable
    
    onChange({ ...distribution, [level]: newVal });
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }} ref={popoverRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "0 12px",
          fontSize: "14px",
          background: "#ffffff",
          border: "1px solid #cbd5e1",
          borderRadius: "8px",
          color: "#334155",
          height: "38px",
          whiteSpace: "nowrap",
          cursor: disabled ? "not-allowed" : "pointer",
          fontWeight: 500,
          boxSizing: "border-box",
          transition: "0.2s"
        }}
        onMouseOver={(e) => { if (!disabled) e.currentTarget.style.borderColor = "#94a3b8"; }}
        onMouseOut={(e) => { if (!disabled) e.currentTarget.style.borderColor = "#cbd5e1"; }}
      >
        <Sliders size={14} color="#64748b" style={{ flexShrink: 0 }} />
        Difficulty ({totalAssigned}/{maxQuestions})
      </button>

      {isOpen && (
        <div 
          style={{
            position: "absolute",
            zIndex: 10,
            marginTop: "8px",
            width: "260px",
            background: "white",
            borderRadius: "8px",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            border: "1px solid #e2e8f0",
            padding: "16px",
            top: "100%",
            left: 0
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h4 style={{ margin: 0, fontWeight: 500, color: "#1e293b", fontSize: "14px" }}>Difficulty Distribution</h4>
            <button 
              onClick={() => setIsOpen(false)} 
              style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: 0 }}
            >
              <X size={14} />
            </button>
          </div>
          
          <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "16px", paddingBottom: "12px", borderBottom: "1px solid #f1f5f9" }}>
            Allocate questions across difficulty levels. Remaining: <span style={{ fontWeight: 700, color: "#4f46e5" }}>{remaining}</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {(['easy', 'medium', 'hard'] as const).map(level => (
              <div key={level} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: "14px", textTransform: "capitalize", color: "#334155", width: "64px" }}>{level}</span>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <button 
                    onClick={() => updateLevel(level, -1)}
                    disabled={(distribution[level] || 0) === 0}
                    style={{
                      padding: "4px",
                      borderRadius: "50%",
                      background: "#f1f5f9",
                      color: "#475569",
                      border: "none",
                      cursor: (distribution[level] || 0) === 0 ? "not-allowed" : "pointer",
                      opacity: (distribution[level] || 0) === 0 ? 0.5 : 1
                    }}
                  >
                    <Minus size={12} />
                  </button>
                  <span style={{ width: "24px", textAlign: "center", fontSize: "14px", fontWeight: 500 }}>{distribution[level] || 0}</span>
                  <button 
                    onClick={() => updateLevel(level, 1)}
                    disabled={remaining === 0 || (maxAvailable ? ((distribution[level] || 0) >= (maxAvailable[level] || 0)) : false)}
                    style={{
                      padding: "4px",
                      borderRadius: "50%",
                      background: "#f1f5f9",
                      color: "#475569",
                      border: "none",
                      cursor: (remaining === 0 || (maxAvailable ? ((distribution[level] || 0) >= (maxAvailable[level] || 0)) : false)) ? "not-allowed" : "pointer",
                      opacity: (remaining === 0 || (maxAvailable ? ((distribution[level] || 0) >= (maxAvailable[level] || 0)) : false)) ? 0.5 : 1
                    }}
                  >
                    <Plus size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface ScoringPopoverProps {
  scheme: ScoringScheme;
  onChange: (scheme: ScoringScheme) => void;
  disabled?: boolean;
}

export const ScoringPopover: React.FC<ScoringPopoverProps> = ({ scheme, onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (level: keyof ScoringScheme, val: string) => {
    const num = parseInt(val);
    const newValue = isNaN(num) ? 0 : num;
    onChange({ ...scheme, [level]: newValue } as ScoringScheme);
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }} ref={popoverRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "0 12px",
          fontSize: "14px",
          background: "#ffffff",
          border: "1px solid #cbd5e1",
          borderRadius: "8px",
          color: "#334155",
          height: "38px",
          whiteSpace: "nowrap",
          cursor: disabled ? "not-allowed" : "pointer",
          fontWeight: 500,
          boxSizing: "border-box",
          transition: "0.2s"
        }}
        onMouseOver={(e) => { if (!disabled) e.currentTarget.style.borderColor = "#94a3b8"; }}
        onMouseOut={(e) => { if (!disabled) e.currentTarget.style.borderColor = "#cbd5e1"; }}
      >
        <Settings size={14} color="#64748b" style={{ flexShrink: 0 }} />
        Scoring
      </button>

      {isOpen && (
        <div 
          style={{
            position: "absolute",
            zIndex: 10,
            marginTop: "8px",
            width: "220px",
            background: "white",
            borderRadius: "8px",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            border: "1px solid #e2e8f0",
            padding: "16px",
            top: "100%",
            left: 0
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h4 style={{ margin: 0, fontWeight: 500, color: "#1e293b", fontSize: "14px" }}>Points per Question</h4>
            <button 
              onClick={() => setIsOpen(false)} 
              style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: 0 }}
            >
              <X size={14} />
            </button>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ width: "16px", height: "16px", borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus size={10} color="white" />
                </div>
                <span style={{ fontSize: "14px", fontWeight: 500, color: "#1e293b" }}>Right Answer Score</span>
              </div>
              <input 
                type="number" 
                value={scheme.right !== undefined ? scheme.right : 1}
                onChange={(e) => handleChange('right', e.target.value)}
                style={{ width: "50px", padding: "4px 8px", borderRadius: "6px", border: "1px solid #22c55e", textAlign: "center", outline: "none", color: "#1e293b", fontSize: "14px", fontWeight: 500, background: "#f0fdf4" }}
                min={0}
                max={30}
              />
            </div>
            
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ width: "16px", height: "16px", borderRadius: "50%", background: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Minus size={10} color="white" />
                </div>
                <span style={{ fontSize: "14px", fontWeight: 500, color: "#1e293b" }}>Wrong Answer Score</span>
              </div>
              <input 
                type="number" 
                value={scheme.wrong !== undefined ? scheme.wrong : 0}
                onChange={(e) => handleChange('wrong', e.target.value)}
                style={{ width: "50px", padding: "4px 8px", borderRadius: "6px", border: "1px solid #ef4444", textAlign: "center", outline: "none", color: "#1e293b", fontSize: "14px", fontWeight: 500, background: "#fef2f2" }}
                min={0}
                max={30}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface LanguageSelectPopoverProps {
  selectedLanguages: string[];
  onChange: (languages: string[]) => void;
  disabled?: boolean;
}

export const LanguageSelectPopover: React.FC<LanguageSelectPopoverProps> = ({ selectedLanguages = [], onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const options = ["Python", "Java", "Javascript"];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleLanguage = (lang: string) => {
    if (disabled) return;
    if (selectedLanguages.includes(lang)) {
      onChange(selectedLanguages.filter(l => l !== lang));
    } else {
      onChange([...selectedLanguages, lang]);
    }
  };

  const getDisplayText = () => {
    if (!selectedLanguages || selectedLanguages.length === 0) return "Select Languages";
    if (selectedLanguages.length === options.length) return "All Languages";
    return selectedLanguages.join(", ");
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }} ref={popoverRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          padding: "0 12px",
          fontSize: "14px",
          background: "#ffffff",
          border: "1px solid #cbd5e1",
          borderRadius: "8px",
          color: selectedLanguages && selectedLanguages.length > 0 ? "#1e293b" : "#94a3b8",
          height: "38px",
          minWidth: "150px",
          whiteSpace: "nowrap",
          cursor: disabled ? "not-allowed" : "pointer",
          fontWeight: 500,
          boxSizing: "border-box",
          transition: "0.2s"
        }}
        onMouseOver={(e) => { if (!disabled) e.currentTarget.style.borderColor = "#94a3b8"; }}
        onMouseOut={(e) => { if (!disabled) e.currentTarget.style.borderColor = "#cbd5e1"; }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", maxWidth: "120px" }}>{getDisplayText()}</span>
        <ChevronDown size={14} color="#64748b" style={{ flexShrink: 0 }} />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            zIndex: 20,
            marginTop: "6px",
            width: "170px",
            background: "white",
            borderRadius: "8px",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            border: "1px solid #e2e8f0",
            padding: "6px 0",
            top: "100%",
            left: 0
          }}
        >
          {options.map((lang) => {
            const isChecked = selectedLanguages.includes(lang);
            return (
              <label
                key={lang}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "8px 14px",
                  fontSize: "14px",
                  color: "#334155",
                  cursor: "pointer",
                  transition: "0.15s",
                  background: isChecked ? "#f8fafc" : "transparent"
                }}
                onMouseOver={(e) => e.currentTarget.style.background = "#f1f5f9"}
                onMouseOut={(e) => e.currentTarget.style.background = isChecked ? "#f8fafc" : "transparent"}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleLanguage(lang)}
                  style={{ accentColor: "var(--primary)", width: "16px", height: "16px", cursor: "pointer" }}
                />
                {lang}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};
